import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";

/** 실제 상세 도구 높이와 Inspector viewport를 비교한다. 팔레트 높이를 다시 임계로 쓰지 않는다. */
export function useAnnotationToolsPresentation(
  active: boolean,
  targetCount: number,
  authoring: boolean,
) {
  const contentId = useId();
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const measuredWidthRef = useRef(0);
  const [override, setOverride] = useState<boolean | null>(null);
  const [previewCompact, setPreviewCompact] = useState(false);
  const [availableHeight, setAvailableHeight] = useState(0);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const [compactHeight, setCompactHeight] = useState(0);
  const [measuringCompact, setMeasuringCompact] = useState(false);
  const compact =
    measuringCompact ||
    previewCompact ||
    (override ??
      (expandedHeight > 0 && availableHeight > 0 && expandedHeight > availableHeight + 1));

  useLayoutEffect(() => {
    // 도구 구성 자체가 달라지면 상세 모양을 다시 측정한다. 사용자가 고른 모양은 그대로 유지한다.
    setExpandedHeight(0);
    setCompactHeight(0);
  }, [active, targetCount, authoring]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!active || !viewport || !content) return;
    const update = () => {
      const inspector = viewport.closest<HTMLElement>("[data-annotation-inspector]");
      const frames = inspector?.parentElement?.querySelector<HTMLElement>(
        "[data-captured-frame-section]",
      );
      // 목록이 축약으로 얻은 공간을 사용해도 자동 펼침 기준은 전체 가용 예산으로 유지한다.
      const height =
        inspector && frames
          ? inspector.getBoundingClientRect().height +
            frames.getBoundingClientRect().height -
            (Number.parseFloat(getComputedStyle(frames).minHeight) || 0)
          : viewport.clientHeight;
      if (height <= 0) return;
      // 줄바꿈에 필요한 상세 높이는 폭에도 달린다. scrollbar 출현에 흔들리지 않는 외곽 폭을 쓴다.
      const width = viewport.getBoundingClientRect().width;
      if (width > 0 && Math.abs(width - measuredWidthRef.current) > 1) {
        measuredWidthRef.current = width;
        setExpandedHeight(0);
      }
      setAvailableHeight(height);
      const naturalHeight = content.getBoundingClientRect().height;
      if (naturalHeight > 0) {
        if (compact) {
          setCompactHeight(naturalHeight);
          setMeasuringCompact(false);
        } else {
          setExpandedHeight(naturalHeight);
          // 처음 도구가 생기거나 구성이 바뀔 때 paint 전에 실제 팔레트도 한 번 측정한다.
          // 팔레트는 폭에 따라 줄바꿈하지 않으므로 resize 때 입력 중인 상세 도구를 재마운트하지 않는다.
          if (compactHeight === 0) setMeasuringCompact(true);
        }
      }
    };
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(viewport);
    observer?.observe(content);
    const panelBody = viewport.closest("[data-annotation-panel-body]");
    if (panelBody) observer?.observe(panelBody);
    return () => observer?.disconnect();
  }, [active, compact, compactHeight, targetCount, authoring]);

  const setCompact = useCallback((value: boolean) => {
    setPreviewCompact(false);
    setOverride(value);
  }, []);

  return {
    viewportRef,
    contentRef,
    contentId,
    compact,
    compactHeight,
    setCompact,
    setPreviewCompact,
    allowPreviewCompaction: override !== false,
  };
}
